import React, { useState } from "react";
import { FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ImageIcon, X } from "lucide-react";
import UniversalPhotoUploadDialog from "@/components/common/UniversalPhotoUploadDialog";
import SecureImage from "@/components/SecureImage";
import { useTranslation } from "react-i18next";

type PhotoUploaderProps = {
  photoUrls: string[];
  onPhotoUpdate: (newUrls: string[]) => void;
  projectId?: string;
  disabled?: boolean;
};

const PhotoUploader: React.FC<PhotoUploaderProps> = ({ photoUrls, onPhotoUpdate, projectId, disabled = false }) => {
  const { t } = useTranslation();
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  const handlePhotoUploaded = (photoUrl: string) => {
    const newUrls = [...photoUrls, photoUrl];
    onPhotoUpdate(newUrls);
    setPhotoDialogOpen(false);
  };

  const handleRemovePhoto = (indexToRemove: number) => {
    const newUrls = photoUrls.filter((_, index) => index !== indexToRemove);
    onPhotoUpdate(newUrls);
  };

  return (
    <div className="space-y-2">
      <FormLabel className="text-sm text-muted-foreground mb-1">Fotoğraf</FormLabel>
      
      {/* Photos Grid */}
      {photoUrls.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photoUrls.map((url, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
              <SecureImage 
                src={url} 
                alt={`Masraf fotoğrafı ${index + 1}`} 
                className="w-full h-full object-cover" 
              />
              {!disabled && (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 rounded-full"
                  onClick={() => handleRemovePhoto(index)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Photo Button */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setPhotoDialogOpen(true)}
        disabled={disabled}
        className="w-full flex items-center justify-center py-6 border-2 border-dashed"
      >
        <ImageIcon className="h-5 w-5 mr-2" />
        {photoUrls.length > 0 ? 'Başka Fotoğraf Ekle' : 'Fotoğraf Ekle'}
      </Button>

      {/* Photo Upload Dialog */}
      <UniversalPhotoUploadDialog
        isOpen={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onPhotoUploaded={handlePhotoUploaded}
        bucketName="documents"
        folderPath={projectId ? `projects/${projectId}` : "expenses"}
        title="Masraf Fotoğrafı Ekle"
        description="Masraf için bir fotoğraf yükleyebilirsiniz."
        disabled={disabled}
      />
    </div>
  );
};

export default PhotoUploader;